import React, { useEffect, Suspense, lazy } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import MarigoldWebsite from './MarigoldWebsite';
import CookieConsent from './components/ui/CookieConsent';
import { initGA, pageview } from './utils/analytics';

const AIServicesPage = lazy(() => import('./pages/AIServicesPage'));
const SolutionsPage = lazy(() => import('./pages/SolutionsPage'));
const TrainingPage = lazy(() => import('./pages/TrainingPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const BlogManagementPage = lazy(() => import('./pages/BlogManagementPage'));
const EnhancedBlogManagementPage = lazy(() => import('./pages/EnhancedBlogManagementPage'));
const OurAIStoryPage = lazy(() => import('./pages/OurAIStoryPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const DaisyAdManagementPage = lazy(() => import('./pages/DaisyAdManagementPage'));
const DaisyUserResourcesPage = lazy(() => import('./pages/DaisyUserResourcesPage'));
const DaisyFAQPage = lazy(() => import('./pages/DaisyFAQPage'));

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    pageview(location.pathname);
  }, [location]);
  
  return null;
};

// Scroll to top on route change when no hash fragment is present
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.search, location.hash]);

  return null;
};

// Ensure external links open in a new tab for security and UX
const OpenExternalLinksInNewTab = () => {
	const location = useLocation();

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const anchors = Array.from(document.querySelectorAll('a[href]')) as HTMLAnchorElement[];
		const currentOrigin = window.location.origin;

		anchors.forEach((anchor) => {
			const href = anchor.getAttribute('href');
			if (!href) return;
			// Skip in-page anchors and internal app routes
			if (href.startsWith('#') || href.startsWith('/')) return;
			// Skip non-http(s) schemes like mailto:, tel:, javascript:
			if (!/^https?:\/\//i.test(href)) return;

			let url: URL;
			try {
				url = new URL(href, currentOrigin);
			} catch {
				return;
			}

			const isExternal = url.origin !== currentOrigin;
			if (!isExternal) return;

			anchor.setAttribute('target', '_blank');
			const existingRel = anchor.getAttribute('rel') || '';
			const relParts = new Set(existingRel.split(' ').filter(Boolean));
			relParts.add('noopener');
			relParts.add('noreferrer');
			anchor.setAttribute('rel', Array.from(relParts).join(' '));
		});
	}, [location.pathname, location.search, location.hash]);

	return null;
};

function App() {
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
  }, []);

  return (
    <HelmetProvider>
      <Helmet>
        <title>Marigold One11 — AI Solutions, Automation, and Training</title>
        <meta name="description" content="AI-driven business solutions that save time at every step. Services include AI integration, automation, analytics, and training." />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.origin + '/' : '/'} />
        {/* Performance: help speed up first navigation to external destinations */}
        <link rel="preconnect" href="https://www.meetup.com" />
        <link rel="dns-prefetch" href="https://www.meetup.com" />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Marigold One11 — AI Solutions, Automation, and Training" />
        <meta property="og:description" content="AI-driven business solutions that save time at every step." />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:image" content="/assets/images/marigold-logo.png" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Marigold One11 — AI Solutions, Automation, and Training" />
        <meta name="twitter:description" content="AI-driven business solutions that save time at every step." />
        <meta name="twitter:image" content="/assets/images/marigold-logo.png" />
      </Helmet>
      <BrowserRouter>
        <PageViewTracker />
        <ScrollToTop />
        <OpenExternalLinksInNewTab />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<MarigoldWebsite />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/ai-services" element={<AIServicesPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/daisy-ad-management" element={<DaisyAdManagementPage />} />
            <Route path="/daisy-user-resources" element={<DaisyUserResourcesPage />} />
            <Route path="/daisy-faq" element={<DaisyFAQPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/blog-management" element={<BlogManagementPage />} />
            <Route path="/enhanced-blog-management" element={<EnhancedBlogManagementPage />} />
            <Route path="/our-ai-story" element={<OurAIStoryPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <CookieConsent />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;